
import { 
  UpgradableNumber 
} from "@dndModels/misc/UpgradableNumber";

export class AbilityScores {
  _strength: number | null = 8;
  _dexterity: number | null = 8;
  _constitution: number | null = 8;
  _intelligence: number | null = 8;
  _wisdom: number | null = 8;
  _charisma: number | null = 8;

  constructor(obj?: any) {
    if (obj === undefined) {
      this._strength = 8;
      this._dexterity = 8;
      this._constitution = 8;
      this._intelligence = 8;
      this._wisdom = 8;
      this._charisma = 8;
    } else if (obj === null || typeof obj === "number") {
      this._strength = obj;
      this._dexterity = obj;
      this._constitution = obj;
      this._intelligence = obj;
      this._wisdom = obj;
      this._charisma = obj;
    } else if (typeof obj === "object") {
      this._strength = obj.strength;
      this._dexterity = obj.dexterity;
      this._constitution = obj.constitution;
      this._intelligence = obj.intelligence;
      this._wisdom = obj.wisdom;
      this._charisma = obj.charisma;
    }
  }

  getScore(score: number | null): number {
    return score === null ? -30 : score;
  }

  get strength(): number {
    return this.getScore(this._strength);
  }

  get dexterity(): number {
    return this.getScore(this._dexterity);
  }

  get constitution(): number {
    return this.getScore(this._constitution);
  }

  get intelligence(): number {
    return this.getScore(this._intelligence);
  }

  get wisdom(): number {
    return this.getScore(this._wisdom);
  }

  get charisma(): number {
    return this.getScore(this._charisma);
  }

  getMod(score: number | null): number {
    return score === null ? -20 : Math.floor((score - 10) / 2);
  }

  get STR(): number {
    return this.getMod(this._strength);
  }

  get DEX(): number {
    return this.getMod(this._dexterity);
  }

  get CON(): number {
    return this.getMod(this._constitution);
  }

  get INT(): number {
    return this.getMod(this._intelligence);
  }

  get WIS(): number {
    return this.getMod(this._wisdom);
  }

  get CHA(): number {
    return this.getMod(this._charisma);
  }

  getAbilityScore(ability: string) {
    const score = this.getNullableAbilityScore(ability);
    return score !== null ? score : -30;
  }

  getNullableAbilityScore(ability: string) {
    let score: number | null = null;
    switch(ability.toLocaleLowerCase().substring(0, 3)) {
      case "str":
        score = this._strength;
      break;
      case "dex":
        score = this._dexterity;
      break;
      case "con":
        score = this._constitution;
      break;
      case "int":
        score = this._intelligence;
      break;
      case "wis":
        score = this._wisdom;
      break;
      case "cha":
        score = this._charisma;
      break;
    }
    return score;
  }

  setAbilityScore(ability: string, value: number | null) {
    switch(ability.toLocaleLowerCase().substring(0, 3)) {
      case "str":
        this._strength = value;
      break;
      case "dex":
        this._dexterity = value;
      break;
      case "con":
        this._constitution = value;
      break;
      case "int":
        this._intelligence = value;
      break;
      case "wis":
        this._wisdom = value;
      break;
      case "cha":
        this._charisma = value;
      break;
    }
  }

  addAbilityScore(ability: string, value: number) {
    if (ability) {
      switch(ability.toLocaleLowerCase().substring(0, 3)) {
        case "str":
          this._strength = this._strength ? this._strength + value : value;
        break;
        case "dex":
          this._dexterity = this._dexterity ? this._dexterity + value : value;
        break;
        case "con":
          this._constitution = this._constitution ? this._constitution + value : value;
        break;
        case "int":
          this._intelligence = this._intelligence ? this._intelligence + value : value;
        break;
        case "wis":
          this._wisdom = this._wisdom ? this._wisdom + value : value;
        break;
        case "cha":
          this._charisma = this._charisma ? this._charisma + value : value;
        break;
      }
    }
  }

  getModifier(ability: string) {
    let score = -20;
    if (ability.includes(" or ")) {
      const pieces = ability.split(" ");
      for (const piece of pieces.filter(o => o !== "or")) {
        const piece2 = piece.replace(",", "").trim();
        score = Math.max(score, this.getModifier(piece2));
      }
    } else {
      switch(ability.toLocaleLowerCase().substring(0, 3)) {
        case "str":
          score = this.STR;
        break;
        case "dex":
          score = this.DEX;
        break;
        case "con":
          score = this.CON;
        break;
        case "int":
          score = this.INT;
        break;
        case "wis":
          score = this.WIS;
        break;
        case "cha":
          score = this.CHA;
        break;
      }
    }
    return score;
  }

  toDBObj(): any {
    return {
      strength: this._strength,
      dexterity: this._dexterity,
      constitution: this._constitution,
      intelligence: this._intelligence,
      wisdom: this._wisdom,
      charisma: this._charisma
    };
  }

  matches(ability_scores: AbilityScores) {
    return (this._strength === ability_scores._strength &&
      this._dexterity === ability_scores._dexterity &&
      this._constitution === ability_scores._constitution &&
      this._intelligence === ability_scores._intelligence &&
      this._wisdom === ability_scores._wisdom &&
      this._charisma === ability_scores._charisma);
  }

  clone(): AbilityScores {
    const new_AS = new AbilityScores();
    new_AS.copy(this);
    return new_AS;
  }

  copy(copyMe: AbilityScores) {
    this._strength = copyMe._strength;
    this._dexterity = copyMe._dexterity;
    this._constitution = copyMe._constitution;
    this._intelligence = copyMe._intelligence;
    this._wisdom = copyMe._wisdom;
    this._charisma = copyMe._charisma;
  }

  mix(copyMe: AbilityScores) {
    if (copyMe._strength) {
      this._strength = copyMe._strength;
    }
    if (copyMe._dexterity) {
      this._dexterity = copyMe._dexterity;
    }
    if (copyMe._constitution) {
      this._constitution = copyMe._constitution;
    }
    if (copyMe._intelligence) {
      this._intelligence = copyMe._intelligence;
    }
    if (copyMe._wisdom) {
      this._wisdom = copyMe._wisdom;
    }
    if (copyMe._charisma) {
      this._charisma = copyMe._charisma;
    }
  }

  add(copyMe: AbilityScores) {
    if (copyMe._strength) {
      this._strength = this._strength ? this._strength + copyMe._strength : copyMe._strength;
    }
    if (copyMe._dexterity) {
      this._dexterity = this._dexterity ? this._dexterity + copyMe._dexterity : copyMe._dexterity;
    }
    if (copyMe._constitution) {
      this._constitution = this._constitution ? this._constitution + copyMe._constitution : copyMe._constitution;
    }
    if (copyMe._intelligence) {
      this._intelligence = this._intelligence ? this._intelligence + copyMe._intelligence : copyMe._intelligence;
    }
    if (copyMe._wisdom) {
      this._wisdom = this._wisdom ? this._wisdom + copyMe._wisdom : copyMe._wisdom;
    }
    if (copyMe._charisma) {
      this._charisma = this._charisma ? this._charisma + copyMe._charisma : copyMe._charisma;
    }
  }

  minus(copyMe: AbilityScores) {
    if (copyMe._strength) {
      this._strength = this._strength ? this._strength - copyMe._strength : -1 * copyMe._strength;
    }
    if (copyMe._dexterity) {
      this._dexterity = this._dexterity ? this._dexterity - copyMe._dexterity : -1 * copyMe._dexterity;
    }
    if (copyMe._constitution) {
      this._constitution = this._constitution ? this._constitution - copyMe._constitution : -1 * copyMe._constitution;
    }
    if (copyMe._intelligence) {
      this._intelligence = this._intelligence ? this._intelligence - copyMe._intelligence : -1 * copyMe._intelligence;
    }
    if (copyMe._wisdom) {
      this._wisdom = this._wisdom ? this._wisdom - copyMe._wisdom : -1 * copyMe._wisdom;
    }
    if (copyMe._charisma) {
      this._charisma = this._charisma ? this._charisma - copyMe._charisma : -1 * copyMe._charisma;
    }
  }

  plus(copyMe: AbilityScores) {
    if (copyMe._strength) {
      this._strength = this._strength ? this._strength + copyMe._strength : copyMe._strength;
    }
    if (copyMe._dexterity) {
      this._dexterity = this._dexterity ? this._dexterity + copyMe._dexterity : copyMe._dexterity;
    }
    if (copyMe._constitution) {
      this._constitution = this._constitution ? this._constitution + copyMe._constitution : copyMe._constitution;
    }
    if (copyMe._intelligence) {
      this._intelligence = this._intelligence ? this._intelligence + copyMe._intelligence : copyMe._intelligence;
    }
    if (copyMe._wisdom) {
      this._wisdom = this._wisdom ? this._wisdom + copyMe._wisdom : copyMe._wisdom;
    }
    if (copyMe._charisma) {
      this._charisma = this._charisma ? this._charisma + copyMe._charisma : copyMe._charisma;
    }
  }
}

export class AbilityScoresUpgradable {
  strength: UpgradableNumber = new UpgradableNumber(8);
  dexterity: UpgradableNumber = new UpgradableNumber(8);
  constitution: UpgradableNumber = new UpgradableNumber(8);
  intelligence: UpgradableNumber = new UpgradableNumber(8);
  wisdom: UpgradableNumber = new UpgradableNumber(8);
  charisma: UpgradableNumber = new UpgradableNumber(8);

  constructor(obj?: any) {
    if (obj !== null && obj !== undefined) {
      if (typeof obj === "number") {
        this.strength.base = obj;
        this.dexterity.base = obj;
        this.constitution.base = obj;
        this.intelligence.base = obj;
        this.wisdom.base = obj;
        this.charisma.base = obj;
      } else if (obj.strength) {
        this.strength = new UpgradableNumber(obj.strength);
        this.dexterity = new UpgradableNumber(obj.dexterity);
        this.constitution = new UpgradableNumber(obj.constitution);
        this.intelligence = new UpgradableNumber(obj.intelligence);
        this.wisdom = new UpgradableNumber(obj.wisdom);
        this.charisma = new UpgradableNumber(obj.charisma);
      } else if (obj.STR) {
        this.strength = new UpgradableNumber(obj.STR);
        this.dexterity = new UpgradableNumber(obj.DEX);
        this.constitution = new UpgradableNumber(obj.CON);
        this.intelligence = new UpgradableNumber(obj.INT);
        this.wisdom = new UpgradableNumber(obj.WIS);
        this.charisma = new UpgradableNumber(obj.CHA);
      }
    } else {
      this.strength = new UpgradableNumber(8);
      this.dexterity = new UpgradableNumber(8);
      this.constitution = new UpgradableNumber(8);
      this.intelligence = new UpgradableNumber(8);
      this.wisdom = new UpgradableNumber(8);
      this.charisma = new UpgradableNumber(8);
    }
  }

  getAbilityScore(ability: string) {
    let score: UpgradableNumber | null = null;
    switch(ability) {
      case "STR":
      case "Strength":
        score = this.strength;
      break;
      case "DEX":
      case "Dexterity":
        score = this.dexterity;
      break;
      case "CON":
      case "Constitution":
        score = this.constitution;
      break;
      case "INT":
      case "Intelligence":
        score = this.intelligence;
      break;
      case "WIS":
      case "Wisdom":
        score = this.wisdom;
      break;
      case "CHA":
      case "Charisma":
        score = this.charisma;
      break;
    }
    return score;
  }

  setAbilityScore(ability: string, value: UpgradableNumber | number) {
    let score: UpgradableNumber | null = null;
    const use_ability = ability.substring(0,3).toUpperCase();
    switch(use_ability) {
      case "STR":
        score = this.strength;
      break;
      case "DEX":
        score = this.dexterity;
      break;
      case "CON":
        score = this.constitution;
      break;
      case "INT":
        score = this.intelligence;
      break;
      case "WIS":
        score = this.wisdom;
      break;
      case "CHA":
        score = this.charisma;
      break;
    }
    if (score) {
      const copyMe = value instanceof UpgradableNumber ? value : new UpgradableNumber(value);
      score.copy(copyMe);
    }
  }

  toDBObj(): any {
    return {
      strength: this.strength.toDBObj(),
      dexterity: this.dexterity.toDBObj(),
      constitution: this.constitution.toDBObj(),
      intelligence: this.intelligence.toDBObj(),
      wisdom: this.wisdom.toDBObj(),
      charisma: this.charisma.toDBObj()
    };
  }

  get STR(): UpgradableNumber {
    return this.strength;
  }

  get DEX(): UpgradableNumber {
    return this.dexterity;
  }

  get CON(): UpgradableNumber {
    return this.constitution;
  }

  get INT(): UpgradableNumber {
    return this.intelligence;
  }

  get WIS(): UpgradableNumber {
    return this.wisdom;
  }

  get CHA(): UpgradableNumber {
    return this.charisma;
  }

  clone(): AbilityScoresUpgradable {
    const new_AS = new AbilityScoresUpgradable();
    new_AS.strength = this.strength.clone();
    new_AS.dexterity = this.dexterity.clone();
    new_AS.constitution = this.constitution.clone();
    new_AS.intelligence = this.intelligence.clone();
    new_AS.wisdom = this.wisdom.clone();
    new_AS.charisma = this.charisma.clone();
    return new_AS;
  }

  getBasicScore(ability: string) {
    let score = 10;
    switch (ability.substring(0,3).toLocaleUpperCase()) {
      case "STR": score = this.STR.base; break;
      case "DEX": score = this.DEX.base; break;
      case "CON": score = this.CON.base; break;
      case "INT": score = this.INT.base; break;
      case "WIS": score = this.WIS.base; break;
      case "CHA": score = this.CHA.base; break;
      default: score = 0; break;
    }
    return score;
  }

  // getBasicModifier(ability: string) {
  //   const score = this.getBasicScore(ability);
  //   return Math.floor((score - 10) / 2);
  // }

  toBasic() {
    const ability_scores = new AbilityScores();
    ability_scores.setAbilityScore("STR", this.getBasicScore("STR"));
    ability_scores.setAbilityScore("DEX", this.getBasicScore("DEX"));
    ability_scores.setAbilityScore("CON", this.getBasicScore("CON"));
    ability_scores.setAbilityScore("INT", this.getBasicScore("INT"));
    ability_scores.setAbilityScore("WIS", this.getBasicScore("WIS"));
    ability_scores.setAbilityScore("CHA", this.getBasicScore("CHA"));
    return ability_scores;
  }
}